import {
  Button,
  Flex,
  Grid,
  Input,
  Text,
  useMediaQuery,
  Table,
  IconButton,
  Tooltip,
  Image,
} from "@chakra-ui/react";
import { PRIMARY_COLOR } from "../../constants/colors";
import { FaTrash, FaWhatsapp } from "react-icons/fa";
import useCart from "../../hooks/useCart";
import { BsCart2, BsMouse } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdAdd, IoMdRemove } from "react-icons/io";
import { useEffect, useState, useMemo } from "react";
import { CiKeyboard } from "react-icons/ci";
import api from "../../services/api";
import { toast } from "react-toastify";

const normalizeImageUrl = (path) => {
  if (!path) return "";
  const normalized = path.replace(/\\/g, "/");
  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";
  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

const formatPhone = (value) => {
  if (!value) return "-";
  const digits = value.toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return digits.replace(/(\d{2})(\d{0,5})(\d{0,4})/, "($1) $2-$3").trim();
};

export default function Cart() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const { clientHash, sellerId } = useParams();
  const [seller, setSeller] = useState(null);

  // Busca o carrinho salvo no backend ao entrar na página e salva no
  // backend imediatamente a cada alteração — sem cache local.
  const {
    cart,
    addToCart,
    decrementFromCart,
    totalCartValue,
    removeFromCart,
    setQuantity,
  } = useCart(clientHash, sellerId);

  const totalProductItems = cart.reduce((sum, item) => {
    const totalQuantity = Object.values(item.sizes).reduce(
      (total, size) => total + (size.quantity || 0),
      0,
    );
    return sum + totalQuantity;
  }, 0);

  const [isKeyboardMode, setIsKeyboardMode] = useState(false);

  useEffect(() => {
    const getSellerInformations = async () => {
      try {
        const response = await api.get(
          `/client-database/sellers/${sellerId}/${clientHash}`,
        );
        setSeller(response.data.data[0]);
      } catch {}
    };
    getSellerInformations();
  }, [clientHash, sellerId]);

  const groupedProducts = useMemo(() => {
    return Object.values(
      cart.reduce((acc, item) => {
        if (!acc[item.id]) {
          acc[item.id] = {
            ...item,
            colors: [item.color],
            sizes: item.sizes,
          };
        } else {
          if (!acc[item.id].colors.includes(item.color)) {
            acc[item.id].colors.push(item.color);
          }
        }
        return acc;
      }, {}),
    );
  }, [cart]);

  const productIds = useMemo(
    () => Array.from(new Set(groupedProducts.map((p) => p.id))),
    [groupedProducts],
  );

  const [productsData, setProductsData] = useState({});

  useEffect(() => {
    const loadAllProducts = async () => {
      if (productIds.length === 0) {
        setProductsData({});
        return;
      }
      try {
        const responses = await Promise.all(
          productIds.map((id) =>
            api.get(`/client-database/product-variations/${id}/${clientHash}`),
          ),
        );

        const formatted = {};
        responses.forEach((res, idx) => {
          const base = res.data.data || [];
          const id = productIds[idx];
          formatted[id] = {
            variations: base,
          };
        });

        setProductsData(formatted);
      } catch {}
    };

    loadAllProducts();
  }, [clientHash, productIds]);

  const getQuantity = (productId, color, size) => {
    const item = cart.find((i) => i.id === productId && i.color === color);
    return item?.sizes?.[size]?.quantity ?? 0;
  };

  const getColorThumbnail = (variations, color) => {
    const variation = variations.find((v) => v.cor === color);
    return normalizeImageUrl(variation?.url_imagem_variacao);
  };

  const getMainImage = (variations) => {
    return normalizeImageUrl(variations?.[0]?.url_imagem_principal);
  };

  return (
    <Flex
      width="100%"
      padding="15px"
      justifyContent="center"
      minH="100vh"
      paddingTop={isLargerThan768 ? "100px" : "120px"}
    >
      <Flex maxW={isLargerThan768 ? "1200px" : "100%"} width="100%">
        {cart.length === 0 && (
          <Flex
            flexDirection="column"
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap="20px"
          >
            <BsCart2 size={60} />
            <Text>Carrinho vazio...</Text>
            <Button
              backgroundColor={PRIMARY_COLOR}
              padding="10px 15px"
              onClick={() => navigate(`/catalogo/${clientHash}/${sellerId}`)}
            >
              Voltar
            </Button>
          </Flex>
        )}

        {cart.length > 0 && (
          <Grid
            templateColumns={isLargerThan768 ? "1fr 0.5fr" : "1fr"}
            columnGap={isLargerThan768 ? "80px" : "0px"}
            width="100%"
          >
            <Flex flexDirection="column" gap="30px" flex={1}>
              {isKeyboardMode ? (
                <BsMouse
                  size={26}
                  onClick={() => setIsKeyboardMode((prev) => !prev)}
                  cursor="pointer"
                />
              ) : (
                <CiKeyboard
                  size={26}
                  onClick={() => setIsKeyboardMode((prev) => !prev)}
                  cursor="pointer"
                />
              )}

              {groupedProducts.map((product) => {
                const variations = productsData[product.id]?.variations ?? [];

                const totalItemsByProduct = cart
                  .filter((item) => item.id === product.id)
                  .reduce(
                    (sum, item) =>
                      sum +
                      Object.values(item.sizes || {}).reduce(
                        (s, size) => s + (size.quantity || 0),
                        0,
                      ),
                    0,
                  );

                const sizesFromVariations = [
                  ...new Set(variations.map((v) => v.tam).filter(Boolean)),
                ];

                const sizesToRender =
                  sizesFromVariations.length > 0
                    ? sizesFromVariations
                    : Object.keys(product.sizes ?? {});

                const colorsInCart = product.colors ?? [];

                const mainImage = getMainImage(variations);

                return (
                  <Flex
                    key={product.id}
                    flexDirection="column"
                    paddingBottom="20px"
                    gap="10px"
                  >
                    <Flex alignItems="center" justifyContent="space-between">
                      <Flex align="center" gap="10px">
                        {mainImage && (
                          <Image
                            src={mainImage}
                            boxSize="120px"
                            objectFit="cover"
                          />
                        )}
                        <div>
                          <Text fontSize="20px" fontWeight={700}>
                            {product.name}
                          </Text>
                          <Text fontSize="14px" color="gray.500">
                            Referência: {product.prod_referencia}
                          </Text>
                          <Text
                            fontWeight={600}
                            fontSize="18px"
                            color="green.600"
                          >
                            {Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(product.price)}
                          </Text>
                        </div>
                      </Flex>
                      <Flex align="center" gap={3}>
                        <FaTrash
                          onClick={() => removeFromCart(product.id)}
                          cursor="pointer"
                        />
                      </Flex>
                    </Flex>

                    <Table.Root size="lg" showColumnBorder>
                      <Table.Header>
                        <Table.Row backgroundColor="white">
                          <Table.ColumnHeader></Table.ColumnHeader>
                          {sizesToRender.map((size) => (
                            <Table.ColumnHeader
                              key={size}
                              padding="15px"
                              textAlign="center"
                            >
                              {size}
                            </Table.ColumnHeader>
                          ))}
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {colorsInCart.map((color) => {
                          const thumbnail = getColorThumbnail(
                            variations,
                            color,
                          );
                          const colorHex =
                            variations.find((v) => v.cor === color)
                              ?.cor_hexadecimal ?? color;

                          return (
                            <Table.Row key={color} minH="60px">
                              <Table.Cell padding="15px" maxW="80px">
                                <Flex
                                  w="40px"
                                  h="40px"
                                  borderRadius="50%"
                                  bg={thumbnail ? undefined : colorHex}
                                  bgImage={
                                    thumbnail ? `url(${thumbnail})` : undefined
                                  }
                                  bgSize="cover"
                                  bgPosition="center"
                                  bgRepeat="no-repeat"
                                  border="1px solid #ccc"
                                />
                                <Text fontSize="12px" mt="5px">
                                  {color}
                                </Text>
                              </Table.Cell>

                              {sizesToRender.map((size) => {
                                const selectedVariation = variations.find(
                                  ({ tam, cor }) =>
                                    tam === size && cor === color,
                                );

                                const availableStock =
                                  selectedVariation?.qtde ?? 0;

                                const quantity = getQuantity(
                                  product.id,
                                  color,
                                  size,
                                );

                                const handleAdd = () => {
                                  if (availableStock <= 0) {
                                    toast.error(
                                      "Produto sem estoque disponível para essa variação.",
                                    );
                                    return;
                                  }
                                  if (quantity >= availableStock) {
                                    toast.warning("Estoque máximo");
                                    return;
                                  }
                                  addToCart({
                                    id: product.id,
                                    name: product.name,
                                    price: product.price,
                                    color,
                                    size,
                                    ...(selectedVariation ?? {}),
                                  });
                                };

                                const handleChangeQuantityFromInput = (
                                  value,
                                ) => {
                                  const n = Number(value ?? 0);
                                  if (Number.isNaN(n)) return;

                                  if (availableStock === 0) {
                                    if (quantity > 0) {
                                      setQuantity({
                                        id: product.id,
                                        color,
                                        size,
                                        quantity: 0,
                                        ...(selectedVariation ?? {}),
                                      });
                                    }
                                    return;
                                  }

                                  const clamped = Math.max(
                                    0,
                                    Math.min(n, availableStock),
                                  );

                                  if (n > availableStock) {
                                    toast.warning(
                                      "Estoque máximo atingido para esta variação.",
                                    );
                                  } else if (n < 0) {
                                    return;
                                  }

                                  setQuantity({
                                    id: product.id,
                                    color,
                                    size,
                                    quantity: clamped,
                                    ...(selectedVariation ?? {}),
                                  });
                                };

                                const handleDecrement = () => {
                                  if (quantity > 0) {
                                    decrementFromCart(product.id, color, size);
                                  }
                                };

                                return (
                                  <Table.Cell
                                    key={size}
                                    padding="15px"
                                    textAlign="center"
                                  >
                                    {isKeyboardMode ? (
                                      <Flex
                                        align="center"
                                        justify="center"
                                        minH="60px"
                                        flexDirection="column"
                                        backgroundColor={
                                          availableStock === 0 ? "gray.200" : ""
                                        }
                                      >
                                        {availableStock > 0 ? (
                                          <Input
                                            type="number"
                                            min={0}
                                            value={quantity}
                                            max={availableStock}
                                            onChange={(e) =>
                                              handleChangeQuantityFromInput(
                                                e.target.value,
                                              )
                                            }
                                            textAlign="center"
                                            width="60px"
                                            size="sm"
                                          />
                                        ) : (
                                          <Text fontSize="12px">0</Text>
                                        )}
                                      </Flex>
                                    ) : availableStock === 0 ? (
                                      <Flex
                                        minH="60px"
                                        align="center"
                                        justify="center"
                                        backgroundColor="gray.200"
                                        borderRadius="md"
                                      ></Flex>
                                    ) : quantity === 0 ? (
                                      <Flex
                                        direction="column"
                                        gap={1}
                                        align="center"
                                        justify="center"
                                        minH="60px"
                                      >
                                        <IoMdAdd
                                          cursor="pointer"
                                          size={22}
                                          onClick={handleAdd}
                                        />
                                      </Flex>
                                    ) : (
                                      <Flex
                                        direction="column"
                                        align="center"
                                        justify="center"
                                        gap={1}
                                        minH="60px"
                                      >
                                        <IoMdAdd
                                          cursor="pointer"
                                          size={18}
                                          onClick={handleAdd}
                                        />
                                        <Text fontSize="sm">{quantity}</Text>
                                        <IoMdRemove
                                          cursor="pointer"
                                          size={18}
                                          onClick={handleDecrement}
                                        />
                                      </Flex>
                                    )}
                                  </Table.Cell>
                                );
                              })}
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>

                    <Flex>
                      <Text fontSize="14px" fontWeight="600" color="gray.600">
                        {totalItemsByProduct}{" "}
                        {totalItemsByProduct > 1 ? "pçs" : "pç"}
                      </Text>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>

            <Flex flexDirection="column" gap="20px">
              <Flex gap="10px">
                <Input
                  type="text"
                  placeholder="Cupom de desconto"
                  paddingLeft="15px"
                />
                <Button padding="15px 20px" backgroundColor={PRIMARY_COLOR}>
                  Aplicar
                </Button>
              </Flex>
              <Flex gap="10px">
                <Input
                  type="text"
                  placeholder="Adicionar observação"
                  paddingLeft="15px"
                />
              </Flex>
              <hr />
              <Text fontWeight="600">Vendedor</Text>
              <Flex gap="10px">
                <Flex
                  width="50px"
                  height="50px"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="gray"
                  color="white"
                  borderRadius="50%"
                >
                  {seller?.pes_fantasia?.[0] ?? "-"}
                </Flex>
                <Flex flexDirection="column">
                  <Text fontWeight="600">{seller?.pes_fantasia ?? "-"}</Text>
                  <Flex alignItems="center" gap="5px">
                    <FaWhatsapp />
                    <Text>{formatPhone(seller?.tel_numero) ?? "-"}</Text>
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                padding="15px"
                backgroundColor="rgb(244 244 244)"
                flexDirection="column"
                gap="10px"
              >
                <Flex justifyContent="space-between">
                  <Text>Solicitado</Text>
                  <Flex alignItems="center" gap="10px">
                    <Text>
                      {totalProductItems} {totalProductItems > 1 ? "pçs" : "pç"}
                    </Text>
                    <Text>
                      {Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalCartValue())}
                    </Text>
                  </Flex>
                </Flex>
                <hr />
                <Flex justifyContent="space-between">
                  <Text fontWeight="700">Total</Text>
                  <Text fontWeight="600">
                    {Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(totalCartValue())}
                  </Text>
                </Flex>
              </Flex>
              <Flex justifyContent="center">
                <Text>COMPRA SEGURA E GARANTIDA</Text>
              </Flex>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex flexDirection="column">
                  <Text fontWeight="400">Total</Text>
                  <Text fontWeight="700">
                    {Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(totalCartValue())}
                  </Text>
                </Flex>
                <Button
                  padding="10px 15px"
                  backgroundColor={PRIMARY_COLOR}
                  onClick={() =>
                    navigate(`/checkout/${clientHash}/${sellerId}`)
                  }
                >
                  Continuar
                </Button>
              </Flex>
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                padding="10px"
              >
                <BsCart2 />
                Voltar
              </Button>
            </Flex>
          </Grid>
        )}
      </Flex>
    </Flex>
  );
}
